// ---------------------------------------------------------------------------
// Media Library — every image, video and file across the team's work, one grid.
// Two sources of truth, ONE permission rule: media is seen by whoever can see
// the work it belongs to. Task attachments appear here automatically; direct
// uploads are addressed to "Everyone at CG" or to one project's audience.
// Full-screen panel in the MyWork shell mold: fixed inset, header, Esc to
// close; the flag + close action live in uiStore (mediaOpen / closeMedia).
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import type { MediaItem, MediaKind, MediaScope } from '@cg-projects/shared';
import { FileText, Image as ImageIcon, Music, Play, Upload, X } from 'lucide-react';
import { Icon } from '@/core/ui/icons';
import { Button, EmptyState, Input, LoadError, Skeleton, fieldClassSm } from '@/core/ui/atoms';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';
import { useUiStore } from '@/core/ui/uiStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useMediaStore } from './mediaStore';
import { Lightbox } from './Lightbox';
import { UploadZone } from './UploadZone';

const KIND_TABS: Array<{ key: MediaKind | null; label: string }> = [
  { key: null, label: 'All' },
  { key: 'image', label: 'Images' },
  { key: 'video', label: 'Videos' },
  { key: 'audio', label: 'Audio' },
  { key: 'other', label: 'Files' },
];

const SCOPE_TABS: Array<{ key: MediaScope; label: string; hint: string }> = [
  { key: 'all', label: 'Everything', hint: 'Task attachments + shared library uploads' },
  { key: 'library', label: 'Shared library', hint: 'Only files uploaded straight to the library' },
];

// Segmented-button recipe, verbatim from MyWork's List/Calendar toggle.
const SEG_WRAP = 'flex items-center gap-1 rounded-md border border-line p-0.5';
const segBtn = (active: boolean) =>
  `rounded px-2 py-0.5 text-2xs transition ${active ? 'bg-brand-soft text-ink' : 'text-muted hover:bg-hover hover:text-ink'}`;

/** One grid tile. Click → Lightbox; every preview kind renders its own face. */
function Tile({ item, caption, onOpen }: { item: MediaItem; caption: string; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      title={item.name}
      className="group overflow-hidden rounded-lg border border-line bg-canvas text-left transition hover:ring-1 hover:ring-brand/50"
    >
      <div className="relative aspect-square w-full overflow-hidden bg-page">
        {item.kind === 'image' && (
          <img src={item.url} alt={item.name} loading="lazy" className="h-full w-full object-cover" />
        )}
        {item.kind === 'video' && (
          <>
            {/* preload="metadata" paints the first frame as its own poster —
                no thumbnail pipeline needed, the Range/206 route does the work. */}
            <video src={item.url} preload="metadata" muted playsInline className="h-full w-full object-cover" />
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-page/70 text-ink">
                <Icon i={Play} size={16} />
              </span>
            </span>
            <span className="pointer-events-none absolute right-1.5 top-1.5 rounded bg-page/80 px-1.5 py-0.5 text-2xs font-medium text-muted">
              VIDEO
            </span>
          </>
        )}
        {(item.kind === 'audio' || item.kind === 'other') && (
          <span className="flex h-full w-full items-center justify-center text-faint">
            <Icon i={item.kind === 'audio' ? Music : FileText} size={20} />
          </span>
        )}
      </div>
      <div className="px-2.5 py-2">
        <div className="truncate text-sm text-ink">{item.name}</div>
        <div className="truncate text-2xs text-faint">{caption}</div>
      </div>
    </button>
  );
}

export function MediaPage() {
  const close = useUiStore((s) => s.closeMedia);
  // Esc with the lightbox open must close ONLY the lightbox (it has its own
  // handler) — not the whole Media page underneath it. Found live in the 7/27
  // sweep: one Esc ate both layers. Same guard Settings uses for its dialogs;
  // a ref (not state) so the escape handler always reads the current truth.
  const viewerOpenRef = useRef(false);
  useEscapeToClose(() => { if (!viewerOpenRef.current) close(); });

  const areas = useAreaStore((s) => s.areas);
  const items = useMediaStore((s) => s.items);
  const total = useMediaStore((s) => s.total);
  const loading = useMediaStore((s) => s.loading);
  const loadingMore = useMediaStore((s) => s.loadingMore);
  const error = useMediaStore((s) => s.error);
  const filter = useMediaStore((s) => s.filter);
  const load = useMediaStore((s) => s.load);
  const loadMore = useMediaStore((s) => s.loadMore);
  const setFilter = useMediaStore((s) => s.setFilter);
  const requestPicker = useMediaStore((s) => s.requestPicker);

  // Stale-while-revalidate: the store keeps last visit's grid, so reopening the
  // panel paints instantly and this refetch trues it up underneath.
  useEffect(() => { void load(); }, [load]);

  // Debounced search — keystrokes are local, the store (and the server) hear
  // about them 300ms after the typing pauses.
  const [q, setQ] = useState(filter.q);
  useEffect(() => {
    const t = setTimeout(() => {
      if (q !== useMediaStore.getState().filter.q) setFilter({ q });
    }, 300);
    return () => clearTimeout(t);
  }, [q, setFilter]);

  const areaNameById = useMemo(() => new Map(areas.map((a) => [a.id, a.name])), [areas]);

  /** "Everyone at CG" / project name for library items; project · task for
   *  task-derived ones. */
  const audienceLabel = (item: MediaItem): string => {
    if (item.audienceAreaId === null) return 'Everyone at CG';
    return areaNameById.get(item.audienceAreaId) ?? item.areaName ?? 'One project';
  };
  const captionFor = (item: MediaItem): string => {
    if (item.isLibrary) return audienceLabel(item);
    const parts = [item.areaName, item.taskName].filter(Boolean) as string[];
    return parts.join(' · ') || 'Task attachment';
  };

  // Viewer selection is DERIVED from the id, not a held copy — a delete (or a
  // fresher page landing) that drops the item closes the lightbox by itself.
  const [viewerId, setViewerId] = useState<number | null>(null);
  useEffect(() => { viewerOpenRef.current = viewerId !== null; }, [viewerId]);
  const viewer = viewerId !== null ? (items ?? []).find((i) => i.id === viewerId) ?? null : null;

  return (
    <div className="fixed inset-0 z-[55] flex flex-col bg-page animate-fade-in">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-4">
        <span className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Icon i={ImageIcon} size={16} className="text-muted" /> Media
        </span>
        {items !== null && !error && (
          <span className="text-xs text-faint">{total} file{total === 1 ? '' : 's'}</span>
        )}
        <Button
          variant="primary"
          size="sm"
          className="ml-auto"
          icon={<Icon i={Upload} size={14} />}
          onClick={requestPicker}
        >
          Upload
        </Button>
        <button onClick={close} className="rounded p-1 text-muted transition hover:bg-hover hover:text-ink" title="Close">
          <Icon i={X} size={16} />
        </button>
      </header>

      {/* Toolbar — its own row so seven controls never fight a 48px header. */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line px-4 py-2">
        <div className={SEG_WRAP}>
          {KIND_TABS.map((t) => (
            <button
              key={t.label}
              onClick={() => setFilter({ kind: t.key })}
              className={segBtn(filter.kind === t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className={SEG_WRAP}>
          {SCOPE_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter({ scope: t.key })}
              className={segBtn(filter.scope === t.key)}
              title={t.hint}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="w-44">
          <select
            className={fieldClassSm}
            value={filter.areaId === null ? '' : String(filter.areaId)}
            onChange={(e) => setFilter({ areaId: e.target.value === '' ? null : Number(e.target.value) })}
            title="Only media belonging to one project"
          >
            <option value="">All projects</option>
            {areas.map((a) => (
              <option key={a.id} value={String(a.id)}>{a.name}</option>
            ))}
          </select>
        </div>
        <div className="w-56">
          <Input fieldSize="sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search media…" />
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1400px] px-4 py-4">
          {error && <LoadError message={error} onRetry={() => void load()} />}

          {!error && items === null && (
            // Boot ghosts in the grid's real shape, so the swap doesn't jump.
            <div aria-hidden className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
              {Array.from({ length: 12 }, (_, i) => (
                <div key={i} className="overflow-hidden rounded-lg border border-line bg-canvas">
                  <Skeleton className="aspect-square w-full rounded-none" />
                  <div className="space-y-1.5 px-2.5 py-2">
                    <Skeleton className="h-3.5 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!error && items !== null && items.length === 0 && (
            <EmptyState
              icon={<Icon i={ImageIcon} size={20} />}
              title="No media here yet — drop files to share them"
              hint={
                filter.kind !== null || filter.areaId !== null || filter.q.trim() !== '' || filter.scope !== 'all'
                  ? 'Nothing matches these filters — try clearing them.'
                  : 'Anything attached to a task shows up here automatically.'
              }
              action={<Button size="sm" icon={<Icon i={Upload} size={14} />} onClick={requestPicker}>Upload files</Button>}
            />
          )}

          {!error && items !== null && items.length > 0 && (
            <>
              <div
                className={`grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3 transition-opacity ${loading ? 'opacity-60' : ''}`}
              >
                {items.map((item) => (
                  <Tile key={item.id} item={item} caption={captionFor(item)} onOpen={() => setViewerId(item.id)} />
                ))}
              </div>
              {items.length < total && (
                <div className="flex items-center justify-center gap-2 py-4">
                  <Button size="sm" loading={loadingMore} onClick={() => void loadMore()}>
                    Load more
                  </Button>
                  <span className="text-xs tabular-nums text-faint">{items.length} of {total}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Sibling of the scroll area, child of the FIXED root: its absolute
          drag overlay + bottom strip anchor to the panel, not to a scroll
          position they'd drift away with. */}
      <UploadZone />

      {viewer && (
        <Lightbox
          item={viewer}
          audienceLabel={audienceLabel(viewer)}
          onClose={() => setViewerId(null)}
          closePanel={close}
        />
      )}
    </div>
  );
}
